import React, { useRef, useState, useEffect } from 'react';
import { HotTable } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/dist/handsontable.full.min.css';
import supabase from '../config/supabase';
import { categorizeInventoryItems } from '../config/openai';
import { mapRowsWithResolvedVendors, normalizeVendorName } from '../utils/vendorResolution';

// Register all Handsontable modules including CopyPaste
registerAllModules();

const InventorySpreadsheet = ({ onSave, onCancel, currentStore, startEmpty }) => {
  const hotTableRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userStore, setUserStore] = useState(currentStore || null);

  // Load user and store information
  useEffect(() => {
    loadUserAndStore();
  }, []);

  const loadUserAndStore = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setCurrentUser(session.user);

        // Load user's store
        const { data: stores, error } = await supabase
          .from('store_users')
          .select(`
            store_id,
            role,
            stores (
              id, name, address, phone, email
            )
          `)
          .eq('user_id', session.user.id)
          .eq('is_active', true)
          .single();

        if (!error && stores && stores.stores) {
          setUserStore(stores.stores);
        }
      }
    } catch (error) {
      console.error('Error loading user/store:', error);
    }
  };

  // Define the columns based on the new priority order
  const columns = [
    { data: 'description', width: 200 },                // 1. Description - Everyone starts with what the item is
    { data: 'brand', width: 120 },                      // 2. Brand - Usually listed with/after item name
    { data: 'pack_size', width: 100 },                  // 3. Pack/Size - Common on invoices: "6/1 GAL", "4/5 LB"
    { data: 'unit', width: 80 },                        // 4. Unit - If not implied by pack/size, this helps clarify
    { data: 'case_qty', width: 80 },                    // 5. Case Qty - Most familiar input for quantity
    { data: 'split_qty', width: 80 },                   // 6. Split Qty - Useful, but second to full case entry
    { data: 'category', width: 100 },                   // 7. Category - Natural next step in grouping
    { data: 'location', width: 100 },                   // 8. Location - Useful for stock checks
    { data: 'vendor', width: 120 },                     // 9. Vendor - Good for cross-referencing
    { data: 'supc', width: 100 },                       // 10. SUPC - Important for integrations
    { data: 'cust_number', width: 80 },                 // 11. Cust # - Needed for API but unfamiliar
    { data: 'mfr_number', width: 100 },                 // 12. Mfr # - Rarely used directly
    { data: 'per_lb', width: 80 },                      // 13. Per Lb - Specialized (auto-calculate when possible)
    { data: 'case_price', width: 80 },                  // 14. Case $ - Often pulled from past purchases
    { data: 'each_price', width: 80 }                   // 15. Unit Price - Can be calculated or derived
  ];

  // Initialize with 10 empty rows (in new field order)
  const initialData = Array(10).fill().map(() => ({
    description: '',     // 1. Description - required field
    brand: '',           // 2. Brand
    pack_size: '',       // 3. Pack/Size
    unit: '',            // 4. Unit - NEW FIELD
    case_qty: '',        // 5. Case Qty
    split_qty: '',       // 6. Split Qty
    category: '',        // 7. Category
    location: '',        // 8. Location
    vendor: '',          // 9. Vendor
    supc: '',            // 10. SUPC
    cust_number: '',     // 11. Cust #
    mfr_number: '',      // 12. Mfr #
    per_lb: '',          // 13. Per Lb
    case_price: '',      // 14. Case $
    each_price: ''       // 15. Unit Price
  }));

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [userLocations, setUserLocations] = useState([]);

  // Load user locations from store settings when userStore is available
  useEffect(() => {
    if (userStore) {
      loadUserLocations();
      if (!startEmpty) {
        loadExistingInventoryData();
      }
    }
  }, [userStore, startEmpty]);

  const loadExistingInventoryData = async () => {
    if (!userStore) return;

    setLoading(true);
    try {
      console.log('📋 Loading existing inventory data for store:', userStore.id);
      
      const { data: existingItems, error } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('store_id', userStore.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading existing inventory:', error);
        return;
      }

      if (existingItems && existingItems.length > 0) {
        console.log('📋 Loaded', existingItems.length, 'existing inventory items');
        
        // Convert existing items to the format expected by the spreadsheet
        const formattedData = existingItems.map(item => ({
          description: item.description || '',
          brand: item.brand || '',
          pack_size: item.pack_size || '',
          unit: item.unit || '',
          case_qty: item.case_quantity || item.case_qty || '',
          split_qty: item.split_quantity || item.split_qty || '',
          category: item.category || '',
          location: item.location || '',
          vendor: item.vendor || '',
          supc: item.manufacturer_code || item.supc || '',
          cust_number: item.cust_number || '',
          mfr_number: item.mfr_number || '',
          per_lb: item.per_lb || '',
          case_price: item.case_price || '',
          each_price: item.unit_price || item.each_price || ''
        }));

        // Add empty rows to ensure minimum of 10 rows
        const emptyRows = Array(Math.max(0, 10 - formattedData.length)).fill().map(() => ({
          description: '', brand: '', pack_size: '', unit: '', case_qty: '', split_qty: '',
          category: '', location: '', vendor: '', supc: '', cust_number: '', mfr_number: '',
          per_lb: '', case_price: '', each_price: ''
        }));

        setData([...formattedData, ...emptyRows]);
      } else {
        console.log('📋 No existing inventory found, using empty template');
      }
    } catch (error) {
      console.error('Error loading existing inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserLocations = async () => {
    try {
      // Try to load from store_details using account field
      const { data: storeDetails } = await supabase
        .from('store_details')
        .select('inventory_locations')
        .eq('account', 'default-user')
        .single();

      if (storeDetails?.inventory_locations) {
        setUserLocations(storeDetails.inventory_locations);
        return;
      }

      // Default locations if none configured
      setUserLocations([
        'Walk-in Cooler', 'Walk-in Freezer', 'Kitchen Prep Area',
        'Kitchen Line', 'Dry Storage', 'Bar Area', 'Coffee Station',
        'Dish Pit', 'Office/Storage', 'Dining Room'
      ]);
    } catch (error) {
      console.log('Using default locations:', error);
      setUserLocations([
        'Walk-in Cooler', 'Walk-in Freezer', 'Kitchen Prep Area',
        'Kitchen Line', 'Dry Storage', 'Bar Area', 'Coffee Station',
        'Dish Pit', 'Office/Storage', 'Dining Room'
      ]);
    }
  };

  // AI Categorization function
  const runAICategorization = async (items) => {
    setAiProcessing(true);
    try {
      console.log('Running AI categorization for', items.length, 'items');
      const categorizedItems = await categorizeInventoryItems(items, userLocations);
      console.log('AI categorization results:', categorizedItems);
      return categorizedItems;
    } catch (error) {
      console.error('AI categorization failed:', error);
      alert('AI categorization failed: ' + error.message + '\n\nItems will be saved without AI suggestions.');
      return items; // Return original items if AI fails
    } finally {
      setAiProcessing(false);
    }
  };

  const handleSave = async () => {
    const hotInstance = hotTableRef.current.hotInstance;
    const tableData = hotInstance.getData();

    // Convert table data to objects and filter out empty rows
    const formattedData = tableData
      .map((row, index) => {
        const rowData = {};
        columns.forEach((col, colIndex) => {
          rowData[col.data] = row[colIndex] || '';
        });
        return rowData;
      })
      .filter(row => row.description && row.description.trim()); // Only description is required

    if (formattedData.length === 0) {
      alert('Please add at least one item with a Description');
      return;
    }

    // Check if any items need AI categorization
    const itemsNeedingAI = formattedData.filter(item =>
      !item.category || !item.location || item.category.trim() === '' || item.location.trim() === ''
    );

    let finalData = formattedData;

    if (itemsNeedingAI.length > 0) {
      const useAI = confirm(
        `${itemsNeedingAI.length} items are missing category or location.\n\n` +
        `Would you like to use AI to automatically categorize them?\n\n` +
        `Click OK to use AI categorization, or Cancel to save as-is.`
      );

      if (useAI) {
        try {
          console.log('Running AI categorization for', itemsNeedingAI.length, 'items');
          const aiResults = await runAICategorization(itemsNeedingAI);

          // Update the items with AI suggestions
          finalData = formattedData.map(item => {
            const aiItem = aiResults.find(ai => ai.description === item.description);
            if (aiItem) {
              return {
                ...item,
                category: item.category || aiItem.ai_suggested_category || item.category,
                location: item.location || aiItem.ai_suggested_location || item.location
              };
            }
            return item;
          });

          console.log('Updated data with AI suggestions:', finalData);
        } catch (error) {
          console.error('AI categorization failed:', error);
          // Continue with original data if AI fails
        }
      }
    }

    setSaving(true);
    try {
      console.log('💾 Saving inventory data for store:', userStore?.id);
      
      // First, get existing items to determine which are updates vs inserts
      const { data: existingItems, error: fetchError } = await supabase
        .from('inventory_items')
        .select('id, description, brand')
        .eq('store_id', userStore.id);

      if (fetchError) {
        console.error('Error fetching existing items:', fetchError);
        alert('Error loading existing inventory: ' + fetchError.message);
        return;
      }

      const resolvedFinalData = await mapRowsWithResolvedVendors({
        supabaseClient: supabase,
        storeId: userStore.id,
        rows: finalData,
        getVendorInput: (row) => row.vendor,
        setResolvedVendor: (row, resolved) => ({
          ...row,
          vendor: normalizeVendorName(resolved.vendorDisplayName || row.vendor) || null,
          vendor_id: resolved.vendorId || null
        }),
        stubNote: 'Auto-created from spreadsheet import',
        useAI: true
      });

      // Separate items into updates and inserts
      const itemsToUpdate = [];
      const itemsToInsert = [];

      for (const item of resolvedFinalData) {
        // Try to find existing item by description and brand
        const existingItem = existingItems.find(existing => 
          existing.description === item.description && 
          existing.brand === item.brand
        );
        const canonicalVendorName = normalizeVendorName(item.vendor);

        if (existingItem) {
          // Update existing item
          const updatePayload = {
            id: existingItem.id,
            store_id: userStore.id,
            description: item.description,
            brand: item.brand,
            pack_size: item.pack_size,
            unit: item.unit,
            case_quantity: item.case_qty || 0,
            split_quantity: item.split_qty || 0,
            category: item.category,
            location: item.location,
            vendor: canonicalVendorName || null,
            manufacturer_code: item.supc,
            unit_price: item.each_price || 0,
            case_price: item.case_price || 0,
            updated_at: new Date().toISOString()
          };
          if (item.vendor_id) updatePayload.vendor_id = item.vendor_id;
          itemsToUpdate.push(updatePayload);
        } else {
          // Insert new item
          const insertPayload = {
            store_id: userStore.id,
            description: item.description,
            brand: item.brand,
            pack_size: item.pack_size,
            unit: item.unit,
            case_quantity: item.case_qty || 0,
            split_quantity: item.split_qty || 0,
            category: item.category,
            location: item.location,
            vendor: canonicalVendorName || null,
            manufacturer_code: item.supc,
            unit_price: item.each_price || 0,
            case_price: item.case_price || 0
          };
          if (item.vendor_id) insertPayload.vendor_id = item.vendor_id;
          itemsToInsert.push(insertPayload);
        }
      }

      console.log(`📊 Processing ${itemsToUpdate.length} updates and ${itemsToInsert.length} inserts`);

      // Perform updates
      let updateResults = [];
      if (itemsToUpdate.length > 0) {
        const { data: updateData, error: updateError } = await supabase
          .from('inventory_items')
          .upsert(itemsToUpdate, { onConflict: 'id' });

        if (updateError) {
          console.error('Error updating items:', updateError);
          alert('Error updating existing items: ' + updateError.message);
          return;
        }
        updateResults = updateData || [];
      }

      // Perform inserts
      let insertResults = [];
      if (itemsToInsert.length > 0) {
        const { data: insertData, error: insertError } = await supabase
          .from('inventory_items')
          .insert(itemsToInsert);

        if (insertError) {
          console.error('Error inserting items:', insertError);
          if (insertError.message?.includes('IOS_FREE_ITEM_LIMIT_REACHED')) {
            if (window.PaywallModal?.showIosItemLimit) {
              window.PaywallModal.showIosItemLimit();
            } else {
              alert('Item limit reached\n\nYour free account includes up to 100 inventory items. Remove an existing item to add a new one.');
            }
          } else {
            alert('Error inserting new items: ' + insertError.message);
          }
          return;
        }
        insertResults = insertData || [];
      }

      const aiUsedCount = itemsNeedingAI.length;
      let successMessage = `✅ Successfully saved inventory!\n\n`;
      successMessage += `📝 Updated: ${itemsToUpdate.length} items\n`;
      successMessage += `➕ Added: ${itemsToInsert.length} items\n`;
      if (aiUsedCount > 0) {
        successMessage += `\nAI categorized ${aiUsedCount} items automatically.`;
      }
      
      alert(successMessage);
      console.log('✅ Inventory saved successfully');

      // Call the onSave callback if provided
      if (onSave) {
        await onSave([...updateResults, ...insertResults]);
      }

    } catch (error) {
      console.error('Error saving inventory:', error);
      alert('Error saving inventory: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    const hotInstance = hotTableRef.current.hotInstance;
    hotInstance.alter('insert_row_below');
  };

  const removeRow = () => {
    const hotInstance = hotTableRef.current.hotInstance;
    const selected = hotInstance.getSelected();
    if (selected && selected.length > 0) {
      hotInstance.alter('remove_row', selected[0][0]);
    } else {
      alert('Please select a row to remove');
    }
  };

  const clearAll = () => {
    if (confirm('Are you sure you want to clear all data?')) {
      setData(initialData);
    }
  };

  return (
    <div className="inventory-spreadsheet">
      <div className="mb-3">
        <div className="alert alert-info">
          <div className="row">
            <div className="col-12">
              <h6><i className="feather icon-info me-2"></i>Use this section to enter items by hand or copy/paste directly from your spreadsheets.</h6>
              
              <div className="row mt-3">
                <div className="col-md-4">
                  <h6 className="fw-bold small text-uppercase text-primary">Required Fields</h6>
                  <ul className="small mb-3 ps-3">
                    <li><strong>Description</strong> is the only required field (must be in the first column).</li>
                    <li>All other fields—Brand, Pack/Size, Unit, Quantities, Category, and Location—can be filled in as needed.</li>
                  </ul>

                  <h6 className="fw-bold small text-uppercase text-primary">Adding & Removing Items</h6>
                  <ul className="small mb-0 ps-3">
                    <li><strong>Add Row</strong> → create a new blank item</li>
                    <li><strong>Remove Row</strong> → delete the selected item(s)</li>
                    <li>All changes save directly to your Supabase database.</li>
                  </ul>
                </div>

                <div className="col-md-4">
                  <h6 className="fw-bold small text-uppercase text-primary">Field Order</h6>
                  <p className="small mb-2">When entering or pasting data, use the following column order:</p>
                  <ol className="small mb-3 ps-3">
                    <li>Description</li>
                    <li>Brand</li>
                    <li>Pack / Size</li>
                    <li>Unit (bottle, case, pound, etc.)</li>
                    <li>Quantities (on-hand value if known)</li>
                    <li>Category / Location (e.g., Liquor Closet, Walk-In, Bar Rail)</li>
                  </ol>
                  <p className="small text-muted fst-italic">Missing categories or locations will be automatically assigned, and you can edit them anytime.</p>
                </div>

                <div className="col-md-4">
                  <h6 className="fw-bold small text-uppercase text-primary">Copy & Paste Support</h6>
                  <p className="small mb-2">You can paste data from Excel, Google Sheets, or any spreadsheet.</p>
                  
                  <div className="bg-white p-2 rounded border mb-2">
                    <strong className="d-block small mb-1">Keyboard Shortcuts:</strong>
                    <div className="d-flex justify-content-between small">
                      <span>Copy:</span> <code>Ctrl+C / Cmd+C</code>
                    </div>
                    <div className="d-flex justify-content-between small">
                      <span>Paste:</span> <code>Ctrl+V / Cmd+V</code>
                    </div>
                    <div className="d-flex justify-content-between small">
                      <span>Undo:</span> <code>Ctrl+Z / Cmd+Z</code>
                    </div>
                  </div>
                  
                  <p className="small mb-2">Paste in any number of rows—your table will expand automatically.</p>
                  
                  <div className="alert alert-warning py-2 mb-0">
                    <strong className="d-block small">Troubleshooting Paste Issues</strong>
                    <ul className="small mb-0 ps-3">
                      <li>Click inside the table first to activate it</li>
                      <li>Try right-click → Paste instead of keyboard shortcuts</li>
                      <li>Make sure your browser has permission to access the clipboard</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <div className="btn-group me-2">
          <button className="btn btn-outline-secondary" onClick={addRow}>
            <i className="feather icon-plus me-1"></i>
            Add Row
          </button>
          <button className="btn btn-outline-secondary" onClick={removeRow}>
            <i className="feather icon-minus me-1"></i>
            Remove Row
          </button>
          <button className="btn btn-outline-warning" onClick={clearAll}>
            <i className="feather icon-trash-2 me-1"></i>
            Clear All
          </button>
        </div>
      </div>

      <div className="spreadsheet-container" style={{ height: '500px', overflow: 'auto', width: '1550px', maxWidth: '100%' }}>
        <HotTable
          ref={hotTableRef}
          data={data}
          columns={columns}
          colHeaders={['Description*', 'Brand', 'Pack/Size', 'Unit', 'Case Qty', 'Split Qty', 'Category', 'Location', 'Vendor', 'SUPC', 'Cust #', 'Mfr #', 'Per Lb', 'Case $', 'Unit Price']}
          rowHeaders={true}
          contextMenu={true}
          manualRowResize={true}
          manualColumnResize={true}
          width="100%"
          height="450"
          licenseKey="non-commercial-and-evaluation"
          settings={{
            // Explicitly enable CopyPaste plugin
            copyPaste: {
              enabled: true,
              pasteMode: 'shift_down',
              rowsLimit: 1000,
              columnsLimit: 20,
              uiContainer: document.body
            },
            // Enable context menu with copy/paste options
            contextMenu: {
              items: {
                'row_above': {
                  name: 'Insert row above'
                },
                'row_below': {
                  name: 'Insert row below'
                },
                'separator1': '---------',
                'copy': {
                  name: 'Copy'
                },
                'cut': {
                  name: 'Cut'
                },
                'paste': {
                  name: 'Paste'
                },
                'separator2': '---------',
                'remove_row': {
                  name: 'Remove row'
                },
                'clear_column': {
                  name: 'Clear column'
                },
                'separator3': '---------',
                'undo': {
                  name: 'Undo'
                },
                'redo': {
                  name: 'Redo'
                }
              }
            },
            fillHandle: {
              direction: 'vertical',
              autoInsertRow: true
            },
            autoWrapRow: true,
            autoWrapCol: true,
            enterBeginsEditing: false,
            outsideClickDeselects: false,
            selectionMode: 'multiple',
            startRows: 10,
            startCols: 13,
            minRows: 10,
            maxRows: 1000,
            allowInsertRow: true,
            allowInsertColumn: false,
            allowRemoveRow: true,
            allowRemoveColumn: false,
            undo: true,
            redo: true,
            // Ensure proper focus handling for clipboard
            tabMoves: { row: 0, col: 1 },
            enterMoves: { row: 1, col: 0 }
          }}
        />
      </div>

      <div className="mt-3">
        <div className="d-flex justify-content-between">
          <button 
            className="btn btn-secondary" 
            onClick={onCancel}
            disabled={saving}
          >
            <i className="feather icon-x me-1"></i>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || aiProcessing}
          >
            {aiProcessing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                <span className="ai-dot" style={{marginRight:'6px'}} />
                AI Processing...
              </>
            ) : saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="feather icon-save me-1"></i>
                Save Inventory
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventorySpreadsheet;
